import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { useActiveContext } from '@/auth/ActiveContextProvider';

/**
 * The `/clinical-study` parent route's own `element` (mirrors `AdminGate.tsx`'s active-context
 * readiness convenience, SL-0023 Resolution 9) — every clinical-study page past this gate can
 * assume `useActiveContext().active` is non-null. UNLIKE `AdminGate`, this gate performs NO
 * permission or role check — `useIsOperator()`'s mechanism (a Global-scope role-assignment check
 * over `GET /v1/profile`) is specific to the platform-operator's Global `platform-support` role
 * and does not generalize to the four Organization-scoped clinical-study module roles (SL-0041
 * Resolution R6): `GET /v1/profile`'s `roleAssignments` cannot distinguish one from another
 * client-side, and the endpoint that could (`core-api`'s effective-permission-set resolution) is
 * trusted-service-only, authenticated with a Google-signed Cloud Run ID token no browser client
 * can mint. This is therefore a NAVIGATION-VISIBILITY convenience only (route reachability +
 * active-context readiness); every `clinical-study:*`-gated write in this section surfaces the
 * server's own denial inline on the offending form — the server is the sole authority (FR-4.6;
 * plan Decision 10).
 */
export function ClinicalStudyGate() {
  const { t } = useTranslation('clinical-study');
  const activeContext = useActiveContext();

  if (activeContext.status === 'loading') {
    return <p className="p-6 text-sm">{t('gate.checkingContext')}</p>;
  }

  if (activeContext.status === 'error') {
    return (
      <div
        className="border-border bg-card text-foreground mx-auto mt-6 flex max-w-md items-center
          gap-3 rounded-lg border p-4 text-sm"
      >
        <CircleAlert className="text-destructive size-5 shrink-0" aria-hidden="true" />
        <span>{t('gate.contextLoadError')}</span>
      </div>
    );
  }

  if (activeContext.eligible.length === 0) {
    return <p className="p-6 text-sm text-muted-foreground">{t('gate.noContext')}</p>;
  }

  // Enforces the documented header-precondition invariant (see the doc comment above): every
  // clinical-study page past this gate assumes `active` is non-null. `status: 'ready'` with a
  // non-empty `eligible` list never legitimately pairs with a `null` `active` in practice
  // (`ActiveContextProvider` always resolves one), but this defensive check keeps the gate itself
  // — not each individual page — the single place that enforces the precondition, so a future
  // regression in that resolution logic fails closed here instead of surfacing as a crash deeper
  // in the tree.
  if (activeContext.active === null) {
    return <p className="p-6 text-sm text-muted-foreground">{t('gate.noContext')}</p>;
  }

  return <Outlet />;
}
